"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth/AuthContext";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { getLabel } from "@/lib/labels/getLabel";
import { useHasActivity } from "@/lib/auth/useHasActivity";
interface AuthControlsProps {
  basePath?: string;
  /** Color variant for different header backgrounds */
  variant?: "dark" | "light" | "branded";
  /** Layout mode - dropdown for desktop headers, inline for mobile menus */
  layout?: "dropdown" | "inline";
  /** Explicit text color from header style slot — overrides variant-based classes */
  textColor?: string;
}

/**
 * Authentication controls for the header.
 * Shows login/register buttons for unauthenticated users,
 * and a user dropdown for authenticated users.
 *
 * If the tenant has no Firebase configuration (isAuthEnabled = false),
 * this component renders nothing.
 */
export function AuthControls(t0) {
  const $ = _c(110);
  const {
    basePath: t1,
    variant: t2,
    layout: t3,
    textColor
  } = t0;
  const basePath = t1 === undefined ? "" : t1;
  const variant = t2 === undefined ? "dark" : t2;
  const layout = t3 === undefined ? "dropdown" : t3;
  const {
    user,
    loading,
    isAuthEnabled,
    signOut
  } = useAuth();
  const labels = useLabels();
  const {
    features
  } = useTenantSettings();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const userHasActivity = useHasActivity(user?.uid);
  let t4;
  if ($[0] !== labels.auth?.controls) {
    t4 = labels.auth?.controls ?? {};
    $[0] = labels.auth?.controls;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t = t4;
  const showNudge = user?.isAnonymous === true && features.authentication.upgradeNudge === true && userHasActivity;
  if (!isAuthEnabled) {
    return null;
  }
  if (loading) {
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex items-center gap-2"><div className="w-20 h-8 bg-muted animate-pulse rounded-lg" /></div>;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    return t5;
  }
  let t5;
  if ($[3] !== signOut) {
    t5 = async () => {
      setIsDropdownOpen(false);
      await signOut();
    };
    $[3] = signOut;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleSignOut = t5;
  let t6;
  if ($[5] !== textColor) {
    t6 = textColor ? {
      color: textColor
    } : undefined;
    $[5] = textColor;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const colorStyle = t6;
  let t7;
  if ($[7] !== textColor) {
    t7 = textColor ? {
      color: textColor,
      opacity: 0.7
    } : undefined;
    $[7] = textColor;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const mutedColorStyle = t7;
  const textClass = textColor ? "" : variant === "light" ? "text-white" : variant === "branded" ? "text-primary-foreground" : "text-foreground";
  const mutedTextClass = textColor ? "" : variant === "light" ? "text-white/70" : variant === "branded" ? "text-primary-foreground/70" : "text-muted-foreground";
  const hoverBgClass = variant === "light" ? "hover:bg-white/10" : variant === "branded" ? "hover:bg-primary-foreground/10" : "hover:bg-muted";
  if (user && !user.isAnonymous) {
    if (layout === "inline") {
      let t8;
      if ($[9] !== user.displayName || $[10] !== user.email) {
        t8 = user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U";
        $[9] = user.displayName;
        $[10] = user.email;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      let t9;
      if ($[12] !== t8) {
        t9 = <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">{t8}</div>;
        $[12] = t8;
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      const t10 = `text-sm font-medium ${textClass}`;
      const t11 = user.displayName || user.email;
      let t12;
      if ($[14] !== colorStyle || $[15] !== t10 || $[16] !== t11) {
        t12 = <span className={t10} style={colorStyle}>{t11}</span>;
        $[14] = colorStyle;
        $[15] = t10;
        $[16] = t11;
        $[17] = t12;
      } else {
        t12 = $[17];
      }
      let t13;
      if ($[18] !== t12 || $[19] !== t9) {
        t13 = <div className="flex items-center gap-3 py-2">{t9}{t12}</div>;
        $[18] = t12;
        $[19] = t9;
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      const t14 = `${basePath}/profile`;
      const t15 = `block py-2 px-3 text-sm ${textClass} ${hoverBgClass} rounded-lg transition`;
      const t16 = t.myProfile || "My Profile";
      let t17;
      if ($[21] !== colorStyle || $[22] !== t14 || $[23] !== t15 || $[24] !== t16) {
        t17 = <Link prefetch={false} href={t14} className={t15} style={colorStyle}>{t16}</Link>;
        $[21] = colorStyle;
        $[22] = t14;
        $[23] = t15;
        $[24] = t16;
        $[25] = t17;
      } else {
        t17 = $[25];
      }
      const t18 = `w-full text-left py-2 px-3 text-sm ${textClass} ${hoverBgClass} rounded-lg transition cursor-pointer`;
      const t19 = t.signOut || "Sign Out";
      let t20;
      if ($[26] !== colorStyle || $[27] !== handleSignOut || $[28] !== t18 || $[29] !== t19) {
        t20 = <button type="button" onClick={handleSignOut} className={t18} style={colorStyle}>{t19}</button>;
        $[26] = colorStyle;
        $[27] = handleSignOut;
        $[28] = t18;
        $[29] = t19;
        $[30] = t20;
      } else {
        t20 = $[30];
      }
      let t21;
      if ($[31] !== t17 || $[32] !== t20) {
        t21 = <div className="space-y-1">{t17}{t20}</div>;
        $[31] = t17;
        $[32] = t20;
        $[33] = t21;
      } else {
        t21 = $[33];
      }
      let t22;
      if ($[34] !== t13 || $[35] !== t21) {
        t22 = <div className="space-y-2">{t13}{t21}</div>;
        $[34] = t13;
        $[35] = t21;
        $[36] = t22;
      } else {
        t22 = $[36];
      }
      return t22;
    }
    let t8;
    if ($[37] !== isDropdownOpen) {
      t8 = () => setIsDropdownOpen(!isDropdownOpen);
      $[37] = isDropdownOpen;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    const t9 = `flex items-center gap-2 px-3 py-2 rounded-lg ${hoverBgClass} transition cursor-pointer`;
    let t10;
    if ($[39] !== user.displayName || $[40] !== user.email) {
      t10 = user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U";
      $[39] = user.displayName;
      $[40] = user.email;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    let t11;
    if ($[42] !== t10) {
      t11 = <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">{t10}</div>;
      $[42] = t10;
      $[43] = t11;
    } else {
      t11 = $[43];
    }
    const t12 = `hidden sm:inline text-sm font-medium ${textClass}`;
    const t13 = user.displayName || user.email;
    let t14;
    if ($[44] !== colorStyle || $[45] !== t12 || $[46] !== t13) {
      t14 = <span className={t12} style={colorStyle}>{t13}</span>;
      $[44] = colorStyle;
      $[45] = t12;
      $[46] = t13;
      $[47] = t14;
    } else {
      t14 = $[47];
    }
    const t15 = `w-4 h-4 ${mutedTextClass} transition-transform ${isDropdownOpen ? "rotate-180" : ""}`;
    let t16;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    let t17;
    if ($[49] !== mutedColorStyle || $[50] !== t15) {
      t17 = <svg className={t15} style={mutedColorStyle} fill="none" viewBox="0 0 24 24" stroke="currentColor">{t16}</svg>;
      $[49] = mutedColorStyle;
      $[50] = t15;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    let t18;
    if ($[52] !== t11 || $[53] !== t14 || $[54] !== t17 || $[55] !== t8 || $[56] !== t9) {
      t18 = <button onClick={t8} className={t9}>{t11}{t14}{t17}</button>;
      $[52] = t11;
      $[53] = t14;
      $[54] = t17;
      $[55] = t8;
      $[56] = t9;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    let t19;
    if ($[58] !== basePath || $[59] !== handleSignOut || $[60] !== isDropdownOpen || $[61] !== t) {
      t19 = isDropdownOpen && <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50"><Link prefetch={false} href={`${basePath}/profile`} className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition cursor-pointer" onClick={() => setIsDropdownOpen(false)}>{t.myProfile || "My Profile"}</Link><hr className="my-1 border-border" /><button type="button" onClick={handleSignOut} className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition cursor-pointer">{t.signOut || "Sign Out"}</button></div>;
      $[58] = basePath;
      $[59] = handleSignOut;
      $[60] = isDropdownOpen;
      $[61] = t;
      $[62] = t19;
    } else {
      t19 = $[62];
    }
    let t20;
    if ($[63] !== t18 || $[64] !== t19) {
      t20 = <div className="relative">{t18}{t19}</div>;
      $[63] = t18;
      $[64] = t19;
      $[65] = t20;
    } else {
      t20 = $[65];
    }
    return t20;
  }
  if (showNudge) {
    if (layout === "inline") {
      const t8 = `${basePath}/sign-up`;
      let t9;
      if ($[66] !== labels) {
        t9 = getLabel(labels, "auth.anonymous.nudge.label");
        $[66] = labels;
        $[67] = t9;
      } else {
        t9 = $[67];
      }
      let t10;
      if ($[68] !== t8 || $[69] !== t9) {
        t10 = <Link prefetch={false} href={t8} className="fu-header-nudge__primary block py-2 px-3 text-sm font-medium text-center btn-primary cursor-pointer">{t9}</Link>;
        $[68] = t8;
        $[69] = t9;
        $[70] = t10;
      } else {
        t10 = $[70];
      }
      const t11 = `${basePath}/sign-in`;
      let t12;
      if ($[71] !== labels) {
        t12 = getLabel(labels, "auth.anonymous.nudge.secondary");
        $[71] = labels;
        $[72] = t12;
      } else {
        t12 = $[72];
      }
      let t13;
      if ($[73] !== t11 || $[74] !== t12) {
        t13 = <Link prefetch={false} href={t11} className="fu-header-nudge__secondary block py-2 px-3 text-sm font-medium text-center btn-plain cursor-pointer">{t12}</Link>;
        $[73] = t11;
        $[74] = t12;
        $[75] = t13;
      } else {
        t13 = $[75];
      }
      let t14;
      if ($[76] !== t10 || $[77] !== t13) {
        t14 = <div className="fu-header-nudge space-y-2">{t10}{t13}</div>;
        $[76] = t10;
        $[77] = t13;
        $[78] = t14;
      } else {
        t14 = $[78];
      }
      return t14;
    }
    const t8 = `${basePath}/sign-up`;
    let t9;
    if ($[79] !== labels) {
      t9 = getLabel(labels, "auth.anonymous.nudge.label");
      $[79] = labels;
      $[80] = t9;
    } else {
      t9 = $[80];
    }
    let t10;
    if ($[81] !== t8 || $[82] !== t9) {
      t10 = <Link prefetch={false} href={t8} className="fu-header-nudge__primary px-4 py-2 text-sm font-medium btn-primary cursor-pointer">{t9}</Link>;
      $[81] = t8;
      $[82] = t9;
      $[83] = t10;
    } else {
      t10 = $[83];
    }
    const t11 = `${basePath}/sign-in`;
    let t12;
    if ($[84] !== labels) {
      t12 = getLabel(labels, "auth.anonymous.nudge.secondary");
      $[84] = labels;
      $[85] = t12;
    } else {
      t12 = $[85];
    }
    let t13;
    if ($[86] !== t11 || $[87] !== t12) {
      t13 = <Link prefetch={false} href={t11} className="fu-header-nudge__secondary btn-header px-4 py-2 text-sm font-medium cursor-pointer">{t12}</Link>;
      $[86] = t11;
      $[87] = t12;
      $[88] = t13;
    } else {
      t13 = $[88];
    }
    let t14;
    if ($[89] !== t10 || $[90] !== t13) {
      t14 = <div className="fu-header-nudge flex items-center gap-2">{t10}{t13}</div>;
      $[89] = t10;
      $[90] = t13;
      $[91] = t14;
    } else {
      t14 = $[91];
    }
    return t14;
  }
  if (layout === "inline") {
    const t8 = `${basePath}/sign-in`;
    const t9 = t.logIn || "Log In";
    let t10;
    if ($[92] !== t8 || $[93] !== t9) {
      t10 = <Link prefetch={false} href={t8} className="block py-2 px-3 text-sm font-medium text-center btn-plain cursor-pointer">{t9}</Link>;
      $[92] = t8;
      $[93] = t9;
      $[94] = t10;
    } else {
      t10 = $[94];
    }
    const t11 = `${basePath}/sign-up`;
    const t12 = t.signUp || "Sign Up";
    let t13;
    if ($[95] !== t11 || $[96] !== t12) {
      t13 = <Link prefetch={false} href={t11} className="block py-2 px-3 text-sm font-medium text-center btn-primary cursor-pointer">{t12}</Link>;
      $[95] = t11;
      $[96] = t12;
      $[97] = t13;
    } else {
      t13 = $[97];
    }
    let t14;
    if ($[98] !== t10 || $[99] !== t13) {
      t14 = <div className="space-y-2">{t10}{t13}</div>;
      $[98] = t10;
      $[99] = t13;
      $[100] = t14;
    } else {
      t14 = $[100];
    }
    return t14;
  }
  const t8 = `${basePath}/sign-in`;
  const t9 = t.logIn || "Log In";
  let t10;
  if ($[101] !== t8 || $[102] !== t9) {
    t10 = <Link prefetch={false} href={t8} className="btn-header px-4 py-2 text-sm font-medium cursor-pointer">{t9}</Link>;
    $[101] = t8;
    $[102] = t9;
    $[103] = t10;
  } else {
    t10 = $[103];
  }
  const t11 = `${basePath}/sign-up`;
  const t12 = t.signUp || "Sign Up";
  let t13;
  if ($[104] !== t11 || $[105] !== t12) {
    t13 = <Link prefetch={false} href={t11} className="px-4 py-2 text-sm font-medium btn-primary cursor-pointer">{t12}</Link>;
    $[104] = t11;
    $[105] = t12;
    $[106] = t13;
  } else {
    t13 = $[106];
  }
  let t14;
  if ($[107] !== t10 || $[108] !== t13) {
    t14 = <div className="flex items-center gap-2">{t10}{t13}</div>;
    $[107] = t10;
    $[108] = t13;
    $[109] = t14;
  } else {
    t14 = $[109];
  }
  return t14;
}